import { CircularProgress, Typography } from '@mui/material'
import React, { ChangeEvent, memo, useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { UploadMCNumber, ValidateMCNumber, SkipStep } from 'src/constants/actionTypes'
import { CollectDataStepIndex } from 'src/components/client/CollectDataStepIndex'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { Formik, FormikProps } from 'formik'
import { MCNumberSchema } from './MCNumberForm.validator'
import { MCNumberFormValues } from './MCNumberForm.interface'
import { FormikTextField } from '../shared/form/formik/FormikTextField'
import { MCNumberTextField } from './MCNumberTextField'

import { ShipmentMajor } from '@shopify/polaris-icons'
import { InfoContainer } from '../shared/InfoContainer'
import { Button } from '../shared/Button'
import RecollectIcon from 'src/assets/icons/recollect.svg'
import { ValidateMCNumberResponse } from 'src/interfaces/mcNumber'
import { FormErrorMessage } from '../shared/form/FormErrorMessage'
import { CommonModal } from '../shared/CommonModal'
import { FormikRadioGroup } from '../shared/form/formik/FormikRadioGroup'
import { MCFormType } from './MCNumberForm.constants'
import { OptionalStepLabel } from '../client/OptionalStepLabel'
import { getDriver } from 'src/selectors/driver'
import { DataCollectionSource } from 'src/constants/shared'

const numberOptions = [
  { label: 'I have Motor Carrier Number', value: MCFormType.MC_NUMBER },
  { label: "I don't have Motor Carrier Number", value: MCFormType.NO_NUMBER },
]

const DATE_MASK = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]

const initialValues: MCNumberFormValues = {
  mcNumber: '',
  lastSubmittedDate: '',
  type: MCFormType.MC_NUMBER,
}

const getValidationSchema = (type: MCFormType) => {
  const schemaMap = {
    [MCFormType.MC_NUMBER]: MCNumberSchema,
    [MCFormType.NO_NUMBER]: undefined,
  }

  return schemaMap[type]
}

const MCNumberFormComponent = () => {
  const dispatch = useDispatch()

  const currentStep = useSelector(getCurrentCollectDataStep)
  const driver = useSelector(getDriver)

  const formikRef = useRef<FormikProps<MCNumberFormValues>>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [isMCNumberValidationProcessing, setMCNumberValidationProcessing] = useState<boolean>(false)
  const [isMCNumberChecked, setIsMCNumberChecked] = useState<boolean>(false)
  const [isMCNumberValid, setIsMCNumberValid] = useState<boolean>(true)
  const [showMCNumberInfoModal, setShowMCNumberInfoModal] = useState<boolean>(false)
  const [currentFormType, setCurrentFormType] = useState<MCFormType>(MCFormType.MC_NUMBER)

  const validateMCNumber = useCallback(
    async (mcNumber: string) => {
      setMCNumberValidationProcessing(true)

      try {
        if (driver?.collectDataFlow === DataCollectionSource.Onboarding) {
          const response: ValidateMCNumberResponse = await createAsyncAction(
            dispatch,
            ValidateMCNumber.request({
              mcNumber,
              stepId: currentStep?.id,
            }),
          )
          setIsMCNumberValid(response.isValid)
        }
      } catch (err) {
        setIsMCNumberValid(false)
      } finally {
        setMCNumberValidationProcessing(false)
        setIsMCNumberChecked(true)
      }
    },
    [driver?.collectDataFlow, currentStep?.id],
  )

  const handleReset = () => {
    setIsMCNumberValid(true)
    setIsMCNumberChecked(false)
    formikRef.current?.resetForm({ values: initialValues })
  }

  const handleShowMCNumberInfo = () => {
    setShowMCNumberInfoModal(true)
  }

  const closeMCNumberInfoModal = () => {
    setShowMCNumberInfoModal(false)
  }

  const handleFormSubmit = async (values: MCNumberFormValues) => {
    if (!currentStep?.id) {
      return
    }

    setIsLoading(true)
    setError(null)

    const requestBody: {
      mcNumber: string
      stepId: string
      lastSubmittedAt?: string
    } = {
      ...values,
      mcNumber: values.mcNumber,
      stepId: currentStep?.id,
    }

    if (values.lastSubmittedDate) {
      const [month, day, year] = values.lastSubmittedDate.split('/')
      requestBody.lastSubmittedAt = `${year}-${month}-${day}`
    }

    try {
      if (currentFormType === MCFormType.MC_NUMBER) {
        await createAsyncAction(dispatch, UploadMCNumber.request(requestBody))
      } else if (currentFormType === MCFormType.NO_NUMBER) {
        const requestBody = {
          stepId: currentStep.id,
        }

        await createAsyncAction(dispatch, SkipStep.request(requestBody))
      }
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCheckMCNumber = async () => {
    const errors = await formikRef.current?.validateForm()

    if (!errors?.mcNumber) {
      const currentValues = formikRef.current?.values

      if (currentValues) {
        validateMCNumber(currentValues.mcNumber)
      }
    }
  }

  const handleTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newType = e.target.value as MCFormType
    setCurrentFormType(newType)

    if (formikRef.current) {
      formikRef.current.setFieldValue('type', newType)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={getValidationSchema(currentFormType)}
      innerRef={formikRef}
    >
      {({ handleSubmit, handleChange }) => (
        <div css={tw`h-full w-full flex flex-col justify-between`}>
          <div>
            <div
              css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
            >
              <ShipmentMajor width={20} />
            </div>
            <div css={tw`flex items-center gap-2 mb-2`}>
              <CollectDataStepIndex stepId={currentStep?.id} />
              {currentStep?.optional && <OptionalStepLabel />}
            </div>
            <Typography css={tw`my-2`} variant="h2">
              Verify Motor Carrier Number
            </Typography>
            <Typography css={tw`mb-2 text-shades-400`}>
              Please enter the Motor Carrier Number you are partnered with.
            </Typography>

            <div css={tw`mb-4`}>
              <Typography css={tw`text-shades-400 text-xs font-bold uppercase`} variant="overline">
                Validate MC Number
              </Typography>

              <div css={tw`bg-shades-0 p-4 rounded-xl mt-1.5 relative overflow-hidden`}>
                {currentStep?.optional && (
                  <div css={tw`w-full mb-4`}>
                    <FormikRadioGroup
                      label="Select option"
                      name="type"
                      options={numberOptions}
                      onChange={handleTypeChange}
                    />
                  </div>
                )}

                {currentFormType === MCFormType.MC_NUMBER && (
                  <>
                    <div css={tw`w-full mb-4`}>
                      <Typography
                        css={tw`text-shades-400 w-full font-semibold mb-1`}
                        variant="body2"
                      >
                        Motor Carrier number
                      </Typography>
                      <MCNumberTextField disabled={isMCNumberChecked} />
                    </div>
                    <div css={tw`w-full mb-4`}>
                      <div css={tw`mb-4`}>
                        <FormikTextField
                          name="lastSubmittedDate"
                          placeholder="Enter date"
                          label="Date of last submitted MCS-150 (Motor Carrier Identification Report)"
                          mask={DATE_MASK}
                          onChange={handleChange}
                          css={tw`w-full`}
                        />
                      </div>
                      <div>
                        {!isMCNumberChecked && (
                          <>
                            <InfoContainer onClick={handleShowMCNumberInfo}>
                              Learn more about MC number
                            </InfoContainer>
                            <Button className={tw`w-full mt-4`} onClick={handleCheckMCNumber}>
                              Check MC number
                            </Button>
                          </>
                        )}
                        {!isMCNumberValid && isMCNumberChecked && (
                          <InfoContainer color="error" className={tw`mt-4`}>
                            We couldn't verify your Motor Carrier Number. Please reset and try
                            again.
                          </InfoContainer>
                        )}
                        {isMCNumberValid && isMCNumberChecked && (
                          <InfoContainer color="success" className={tw`mt-4`}>
                            Motor Carrier Number has been verified. You can submit it now.
                          </InfoContainer>
                        )}
                        {isMCNumberChecked && (
                          <Button
                            className={tw`w-full mt-4`}
                            onClick={handleReset}
                            color="grey"
                            startIcon={<img src={RecollectIcon} />}
                          >
                            Reset and Try Again
                          </Button>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {isMCNumberValidationProcessing && (
                  <div
                    css={tw`absolute top-0 left-0 w-full h-full bg-[rgba(255, 255, 255, 0.95)] flex flex-col items-center justify-center px-10`}
                  >
                    <CircularProgress size={22} />
                    <Typography css={tw`text-center mt-4 text-primary-500 font-normal text-base`}>
                      Looking for your Motor Carrier Number info... Please wait while we process
                      your vehicle information.
                    </Typography>
                  </div>
                )}
              </div>
              {(currentFormType === MCFormType.NO_NUMBER ||
                (currentFormType === MCFormType.MC_NUMBER &&
                  isMCNumberValid &&
                  isMCNumberChecked)) && (
                <Button
                  className={tw`w-full mt-4`}
                  loading={isLoading}
                  onClick={() => handleSubmit()}
                >
                  Submit and Continue
                </Button>
              )}
            </div>
          </div>
          <FormErrorMessage css={tw`mb-4`} error={error} />
          <CommonModal
            title="What is a Motor Carrier (MC) Number?"
            text="An MC Number is a unique identifier issued by the FMCSA to companies that operate as for-hire carriers. It shows that the company has the authority to transport goods or passengers across state lines."
            subtext="You can find your MC Number in the approval documents you received from FMCSA or by searching your company name on the FMCSA SAFER website."
            isOpen={showMCNumberInfoModal}
            onClose={closeMCNumberInfoModal}
          />
        </div>
      )}
    </Formik>
  )
}

export const MCNumberForm = memo(MCNumberFormComponent)
